import { useEffect, useMemo, useState } from "react";
import { BellRing, Loader2, Plus, Trash2, AlertTriangle, CheckCircle2, X } from "lucide-react";
import client from "../../lib/api";
import { PageHeader, PrivacyBadge } from "./adminUi";

export default function AdminTriggersScreen() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [metric, setMetric] = useState("");
  const [comparator, setComparator] = useState("below");
  const [threshold, setThreshold] = useState(60);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await client.get("/admin/triggers"); setData(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const catalog = useMemo(() => data?.catalog || [], [data]);
  const metricLabel = useMemo(() => Object.fromEntries(catalog.map((c) => [c.key, c.label])), [catalog]);

  const openForm = () => {
    const first = catalog[0];
    setMetric(first?.key || "");
    setComparator(first?.direction || "below");
    setThreshold(60);
    setLabel("");
    setFormOpen(true);
  };

  const onMetricChange = (key) => {
    setMetric(key);
    const c = catalog.find((x) => x.key === key);
    if (c) setComparator(c.direction);
  };

  const create = async () => {
    setSaving(true);
    try {
      await client.post("/admin/triggers", {
        label: label.trim() || `${metricLabel[metric]} ${comparator} ${threshold}%`,
        metric, comparator, threshold: Number(threshold),
      });
      setFormOpen(false);
      await load();
    } finally { setSaving(false); }
  };

  const toggle = async (id, enabled) => { await client.patch(`/admin/triggers/${id}`, { enabled: !enabled }); await load(); };
  const remove = async (id) => { await client.delete(`/admin/triggers/${id}`); await load(); };

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin" size={20} /></div>;

  const evals = data?.evaluations || [];
  const firing = evals.filter((e) => e.firing && e.enabled);

  return (
    <div data-testid="admin-triggers">
      <PageHeader
        title="Triggers and alerts"
        subtitle="Set rules on aggregate cohort signals and see which are firing right now. Every signal is a group percentage, never an individual."
        actions={<><PrivacyBadge /><button className="k-btn k-btn-primary !py-2" onClick={openForm} data-testid="trigger-new-btn"><Plus size={15} /> New rule</button></>}
      />

      {/* Current metric tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="trigger-metrics">
        {catalog.map((c) => {
          const v = data.metrics[c.key];
          return (
            <div key={c.key} className="k-card" style={{ padding: "0.9rem 1.1rem" }} data-testid={`trigger-metric-${c.key}`}>
              <div className="text-xs" style={{ color: "rgba(19,24,34,0.6)", fontWeight: 600 }}>{c.label}</div>
              <div className="klareet-mono" style={{ fontSize: "1.5rem", color: "#131822", lineHeight: 1.2 }}>{v == null ? "n/a" : `${v}%`}</div>
            </div>
          );
        })}
      </div>

      {firing.length > 0 && (
        <div className="k-card mb-6" style={{ padding: "1.25rem 1.5rem", background: "rgba(179,38,30,0.05)", borderColor: "rgba(179,38,30,0.25)" }} data-testid="trigger-firing-banner">
          <div className="flex items-center gap-2 text-sm font-medium mb-1" style={{ color: "#B3261E" }}><AlertTriangle size={16} /> {firing.length} alert{firing.length > 1 ? "s" : ""} firing</div>
          <div className="k-quiet !text-sm">{firing.map((f) => f.label).join(", ")}</div>
        </div>
      )}

      <div className="k-card" style={{ padding: 0 }} data-testid="trigger-rules-card">
        <div className="px-4 py-3 text-sm font-medium" style={{ color: "#131822", borderBottom: "1px solid rgba(19,24,34,0.08)" }}>Rules</div>
        {evals.length === 0 ? (
          <div className="px-4 py-14 text-center k-quiet" data-testid="trigger-empty">No rules yet. Create one to start watching your cohort.</div>
        ) : (
          <div>
            {evals.map((e) => (
              <div key={e.rule_id} className="flex items-center gap-3 px-4 py-3" style={{ borderBottom: "1px solid rgba(19,24,34,0.05)" }} data-testid={`trigger-rule-${e.rule_id}`}>
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: !e.enabled ? "rgba(19,24,34,0.2)" : e.firing ? "#B3261E" : "#0E7A66" }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" style={{ color: "#131822" }}>{e.label}</div>
                  <div className="k-quiet !text-xs">
                    {metricLabel[e.metric] || e.metric} {e.comparator} {e.threshold}% · now {e.value == null ? "n/a" : `${e.value}%`}
                  </div>
                </div>
                {e.enabled && (
                  <span className="text-xs px-2 py-1 rounded-full font-medium shrink-0 flex items-center gap-1" style={{ color: e.firing ? "#B3261E" : "#0E7A66", background: e.firing ? "rgba(179,38,30,0.1)" : "rgba(14,122,102,0.1)" }} data-testid={`trigger-state-${e.rule_id}`}>
                    {e.firing ? <><AlertTriangle size={12} /> Firing</> : <><CheckCircle2 size={12} /> OK</>}
                  </span>
                )}
                <button className="k-btn k-btn-ghost !py-1 !px-2 !text-xs shrink-0" onClick={() => toggle(e.rule_id, e.enabled)} data-testid={`trigger-toggle-${e.rule_id}`}>
                  {e.enabled ? "Disable" : "Enable"}
                </button>
                <button className="k-btn k-btn-ghost !py-1 !px-2 shrink-0" onClick={() => remove(e.rule_id)} data-testid={`trigger-delete-${e.rule_id}`} aria-label="delete rule"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {formOpen && (
        <div className="k-session-overlay" role="dialog" aria-modal="true" data-testid="trigger-form-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) setFormOpen(false); }}>
          <div className="k-session-card k-fade-in" style={{ maxWidth: "30rem" }}>
            <div className="flex items-start justify-between mb-4">
              <h2 className="klareet-serif" style={{ fontSize: "1.3rem", color: "#131822" }}>New alert rule</h2>
              <button onClick={() => setFormOpen(false)} className="k-btn k-btn-ghost !p-1.5" data-testid="trigger-form-close"><X size={16} /></button>
            </div>
            <label className="k-label mb-1 block">Signal</label>
            <select className="k-input mb-3" value={metric} onChange={(e) => onMetricChange(e.target.value)} data-testid="trigger-metric-select">
              {catalog.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="k-label mb-1 block">When it goes</label>
                <select className="k-input" value={comparator} onChange={(e) => setComparator(e.target.value)} data-testid="trigger-comparator-select">
                  <option value="below">below</option>
                  <option value="above">above</option>
                </select>
              </div>
              <div>
                <label className="k-label mb-1 block">Threshold %</label>
                <input type="number" min={0} max={100} className="k-input" value={threshold} onChange={(e) => setThreshold(e.target.value)} data-testid="trigger-threshold-input" />
              </div>
            </div>
            <label className="k-label mb-1 block">Label (optional)</label>
            <input className="k-input mb-4" value={label} onChange={(e) => setLabel(e.target.value)} placeholder={`${metricLabel[metric] || "Signal"} ${comparator} ${threshold}%`} data-testid="trigger-label-input" />
            <div className="flex justify-end gap-2">
              <button className="k-btn k-btn-ghost" onClick={() => setFormOpen(false)} data-testid="trigger-form-cancel">Cancel</button>
              <button className="k-btn k-btn-primary" onClick={create} disabled={saving || !metric} data-testid="trigger-form-save">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <BellRing size={15} />} Create rule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
